package org.catharsis.spendtel.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.graphics.CompositingStrategy
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay

/**
 * Position of the tooltip relative to the highlighted element
 */
enum class TooltipPosition {
    TOP,
    BOTTOM,
    START,
    END
}

/**
 * State holder for the spotlight tooltip system
 */
class SpotlightState {
    var isVisible by mutableStateOf(false)
        private set
    var targetBounds by mutableStateOf<Rect?>(null)
        private set
    var title by mutableStateOf<String?>(null)
        private set
    var message by mutableStateOf("")
        private set
    var position by mutableStateOf(TooltipPosition.BOTTOM)
        private set
    var highlightPadding by mutableStateOf(8.dp)
        private set
    var highlightCornerRadius by mutableStateOf(12.dp)
        private set
    var onDismiss: () -> Unit = {}
        private set
    
    fun show(
        bounds: Rect,
        title: String? = null,
        message: String,
        position: TooltipPosition = TooltipPosition.BOTTOM,
        highlightPadding: Dp = 8.dp,
        highlightCornerRadius: Dp = 12.dp,
        onDismiss: () -> Unit = {}
    ) {
        this.targetBounds = bounds
        this.title = title
        this.message = message
        this.position = position
        this.highlightPadding = highlightPadding
        this.highlightCornerRadius = highlightCornerRadius
        this.onDismiss = onDismiss
        this.isVisible = true
    }
    
    fun hide() {
        isVisible = false
        onDismiss()
    }
}

val LocalSpotlightState = compositionLocalOf<SpotlightState?> { null }

@Composable
fun rememberSpotlightState(): SpotlightState {
    return remember { SpotlightState() }
}

/**
 * Provider that should wrap your app content to enable spotlight tooltips
 */
@Composable
fun SpotlightTooltipProvider(
    state: SpotlightState = rememberSpotlightState(),
    overlayColor: Color = Color.Black.copy(alpha = 0.85f),
    tooltipBackgroundColor: Color = Color.White,
    tooltipTextColor: Color = Color.Black,
    content: @Composable () -> Unit
) {
    CompositionLocalProvider(LocalSpotlightState provides state) {
        Box(modifier = Modifier.fillMaxSize()) {
            content()
            
            // Render overlay on top of everything
            AnimatedVisibility(
                visible = state.isVisible && state.targetBounds != null,
                enter = fadeIn(animationSpec = tween(300)),
                exit = fadeOut(animationSpec = tween(300))
            ) {
                state.targetBounds?.let { bounds ->
                    SpotlightOverlayContent(
                        targetBounds = bounds,
                        highlightPadding = state.highlightPadding,
                        highlightCornerRadius = state.highlightCornerRadius,
                        overlayColor = overlayColor,
                        tooltipPosition = state.position,
                        tooltipBackgroundColor = tooltipBackgroundColor,
                        tooltipTextColor = tooltipTextColor,
                        title = state.title,
                        message = state.message,
                        onDismiss = { state.hide() }
                    )
                }
            }
        }
    }
}

/**
 * A spotlight tooltip that highlights a specific UI element and shows a tooltip.
 * Must be used within a SpotlightTooltipProvider.
 */
@Composable
fun SpotlightTooltip(
    show: Boolean,
    onDismiss: () -> Unit,
    title: String? = null,
    message: String,
    tooltipPosition: TooltipPosition = TooltipPosition.BOTTOM,
    highlightPadding: Dp = 8.dp,
    highlightCornerRadius: Dp = 12.dp,
    content: @Composable BoxScope.() -> Unit
) {
    val spotlightState = LocalSpotlightState.current
    var targetBounds by remember { mutableStateOf<Rect?>(null) }
    
    // The content that will be highlighted
    Box(
        modifier = Modifier.onGloballyPositioned { coordinates ->
            targetBounds = coordinates.boundsInRoot()
        }
    ) {
        content()
    }
    
    // Use LaunchedEffect to handle state changes properly
    LaunchedEffect(show, targetBounds) {
        if (show && targetBounds != null && spotlightState != null) {
            // Small delay to ensure the layout is complete
            delay(100)
            spotlightState.show(
                bounds = targetBounds!!,
                title = title,
                message = message,
                position = tooltipPosition,
                highlightPadding = highlightPadding,
                highlightCornerRadius = highlightCornerRadius,
                onDismiss = onDismiss
            )
        }
    }
    
    // Handle hiding when show becomes false
    LaunchedEffect(show) {
        if (!show && spotlightState?.isVisible == true) {
            spotlightState.hide()
        }
    }
}

@Composable
private fun SpotlightOverlayContent(
    targetBounds: Rect,
    highlightPadding: Dp,
    highlightCornerRadius: Dp,
    overlayColor: Color,
    tooltipPosition: TooltipPosition,
    tooltipBackgroundColor: Color,
    tooltipTextColor: Color,
    title: String?,
    message: String,
    onDismiss: () -> Unit
) {
    val density = LocalDensity.current
    val paddingPx = with(density) { highlightPadding.toPx() }
    val cornerRadiusPx = with(density) { highlightCornerRadius.toPx() }
    
    // Calculate highlight rect with padding
    val highlightRect = Rect(
        left = targetBounds.left - paddingPx,
        top = targetBounds.top - paddingPx,
        right = targetBounds.right + paddingPx,
        bottom = targetBounds.bottom + paddingPx
    )
    
    Box(
        modifier = Modifier
            .fillMaxSize()
            .graphicsLayer {
                // Required for BlendMode.Clear to work correctly
                compositingStrategy = CompositingStrategy.Offscreen
            }
            .drawBehind {
                drawOverlayWithCutout(
                    overlayColor = overlayColor,
                    highlightRect = highlightRect,
                    cornerRadius = cornerRadiusPx
                )
            }
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onDismiss
            )
    ) {
        TooltipBox(
            highlightRect = highlightRect,
            position = tooltipPosition,
            backgroundColor = tooltipBackgroundColor,
            textColor = tooltipTextColor,
            title = title,
            message = message,
            onClose = onDismiss
        )
    }
}

private fun DrawScope.drawOverlayWithCutout(
    overlayColor: Color,
    highlightRect: Rect,
    cornerRadius: Float
) {
    // Draw the full overlay
    drawRect(color = overlayColor)
    
    // Cut out the highlight area using Clear blend mode
    val cutoutPath = Path().apply {
        addRoundRect(
            RoundRect(
                rect = highlightRect,
                cornerRadius = CornerRadius(cornerRadius, cornerRadius)
            )
        )
    }
    
    drawPath(
        path = cutoutPath,
        color = Color.Transparent,
        blendMode = BlendMode.Clear
    )
    
    // Draw a subtle glow/border around the cutout
    drawRoundRect(
        color = Color.White.copy(alpha = 0.5f),
        topLeft = Offset(highlightRect.left - 3f, highlightRect.top - 3f),
        size = Size(highlightRect.width + 6f, highlightRect.height + 6f),
        cornerRadius = CornerRadius(cornerRadius + 3f, cornerRadius + 3f),
        blendMode = BlendMode.SrcOver
    )
}

@Composable
private fun BoxScope.TooltipBox(
    highlightRect: Rect,
    position: TooltipPosition,
    backgroundColor: Color,
    textColor: Color,
    title: String?,
    message: String,
    onClose: () -> Unit
) {
    val density = LocalDensity.current
    val tooltipSpacing = 20.dp
    val spacingPx = with(density) { tooltipSpacing.toPx() }
    val tooltipWidth = 280.dp
    val tooltipWidthPx = with(density) { tooltipWidth.toPx() }
    
    // Calculate tooltip position based on the target
    val tooltipOffset = when (position) {
        TooltipPosition.BOTTOM -> IntOffset(
            x = (highlightRect.center.x - tooltipWidthPx / 2).toInt().coerceAtLeast(16),
            y = (highlightRect.bottom + spacingPx).toInt()
        )
        TooltipPosition.TOP -> IntOffset(
            x = (highlightRect.center.x - tooltipWidthPx / 2).toInt().coerceAtLeast(16),
            y = (highlightRect.top - spacingPx - 150.dp.value * density.density).toInt()
        )
        TooltipPosition.START -> IntOffset(
            x = (highlightRect.left - spacingPx - tooltipWidthPx).toInt(),
            y = (highlightRect.center.y - 75.dp.value * density.density).toInt()
        )
        TooltipPosition.END -> IntOffset(
            x = (highlightRect.right + spacingPx).toInt(),
            y = (highlightRect.center.y - 75.dp.value * density.density).toInt()
        )
    }
    
    Column(
        modifier = Modifier
            .offset { tooltipOffset }
            .widthIn(max = tooltipWidth)
            .clip(RoundedCornerShape(16.dp))
            .background(backgroundColor)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = {} // Prevent click from propagating to dismiss
            )
            .padding(16.dp),
        horizontalAlignment = Alignment.Start
    ) {
        // Header with close button
        Row(
            modifier = Modifier.padding(bottom = 8.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (title != null) {
                Text(
                    text = title,
                    color = textColor,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.weight(1f)
                )
            } else {
                Spacer(modifier = Modifier.weight(1f))
            }
            
            Box(
                modifier = Modifier
                    .size(28.dp)
                    .clip(CircleShape)
                    .background(Color.Gray.copy(alpha = 0.2f))
                    .clickable(onClick = onClose),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.Default.Close,
                    contentDescription = "Close",
                    tint = textColor.copy(alpha = 0.7f),
                    modifier = Modifier.size(16.dp)
                )
            }
        }
        
        // Message
        Text(
            text = message,
            color = textColor.copy(alpha = 0.8f),
            fontSize = 14.sp,
            lineHeight = 20.sp,
            textAlign = TextAlign.Start
        )
        
        Spacer(modifier = Modifier.height(12.dp))
        
        // Hint text
        Text(
            text = "Tap anywhere to dismiss",
            color = textColor.copy(alpha = 0.5f),
            fontSize = 12.sp
        )
    }
}
